<script lang="ts">
import Component from 'vue-class-component'
import VisualisationCanvas from '@/components/public/Visualisations/VisualisationCanvas.vue'
import { BubblesSimulation } from '@/components/public/Visualisations/BubblesV2/BubblesSimulation'
import { BubblesTransitionOperationsV2 } from '@/components/public/Visualisations/BubblesV2/BubblesTransitionOperationsV2'
import { Coordinate2D, NodeData, Path } from '@/types'
import {
  BubbleMetadata,
  HeaderMetadata,
  BubblesMetadataRegister,
  ClusterMetadata,
  ClusterAndHeaderBoxMetadata,
} from '@/components/public/Visualisations/BubblesV2/Metadata'
import {
  BubbleGroupV2,
  EntitiesRegister,
  HeaderGroupV2,
} from '@/components/public/Visualisations/BubblesV2/Entities'
import { Shapes } from '@/components/public/Visualisations/types'
import {
  BubblesLayout,
  shouldBubblesAmalgamate,
  sortClusters,
} from '@/components/public/Visualisations/BubblesV2/bubblesVisualisationHelpersV2'
import { calculateLayout } from '@/components/public/Visualisations/BubblesV2/bubblesLayoutHelper'
import { settingsService } from '@/services'
import { defaultCoordinate } from '@/constants'
import { BoundingBoxRect } from '@/components/public/Visualisations/Debugging'

interface BubbleAmalgamation {
  ancestor: BubbleMetadata
  descendants: BubbleMetadata[]
}

// eslint-disable-next-line no-use-before-define
@Component<BubblesCanvasV2>({
  watch: {
    storyPath() {
      this.storyPathChanged()
    },
    isRoot() {
      this.simulation.updateIsRoot(this.isRoot)
    },
    config: {
      handler() {
        this.configUpdated()
      },
      deep: true,
    },
    nodeGroups() {
      this.nodeGroupsUpdated()
    },
  },
})
export default class BubblesCanvasV2 extends VisualisationCanvas {
  private simulation: BubblesSimulation = new BubblesSimulation(this.isRoot)

  private layout?: BubblesLayout
  private rootBubblesPosition: Coordinate2D = defaultCoordinate()
  private rootClusterMetadata: ClusterMetadata = new ClusterMetadata(new Path({}))
  private sideClusterMetadata: ClusterMetadata = new ClusterMetadata(new Path({}))
  private metadataRegister: BubblesMetadataRegister = new BubblesMetadataRegister()
  private entitiesRegister: EntitiesRegister = new EntitiesRegister()
  private pendingBubbleAmalgamations: BubbleAmalgamation[] = []
  private pendingBubblesToRemove: BubbleMetadata[] = []
  private allEntitiesBoundingBox: BoundingBoxRect = new BoundingBoxRect('purple')
  private mainClustersBoundingBox: BoundingBoxRect = new BoundingBoxRect('blue')
  private sideBubblesClusterBoundingBox: BoundingBoxRect = new BoundingBoxRect('green')

  mounted() {
    this.initialiseSimulation()
    this.updateHeaders()
    this.updateBubbles()
    this.updateLayout()

    if (settingsService.bubblesCanvasDebugMode) {
      this.addShape(this.mainClustersBoundingBox)
      this.addShape(this.sideBubblesClusterBoundingBox)
      this.addShape(this.allEntitiesBoundingBox)
    }

    this.loading = false
  }

  get storyPath() {
    return this.$typedStore.visualisationData.storyPath
  }

  get isRoot() {
    return this.storyPath.isRoot
  }

  get config() {
    return this.$typedStore.visualisationData.bubblesConfigV2
  }

  get nodeGroups() {
    return this.$typedStore.visualisationData.nodeGroups
  }

  get headerNodes(): NodeData[] {
    if (this.storyPath.isRoot) {
      return []
    }
    const paths: string[] = this.nodeGroups.main.map((g) => g.ancestorPath)
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.allNodes.filter(
      (node) => paths.some((path) => path === node.path),
    )
  }

  recenter() {
    if (this.layout) {
      this.startFocusAnimation(this.layout.zoomFactor, this.layout.offset)
    } else {
      this.startFocusAnimation(this.defaultUserZoomScale, this.defaultPosition)
    }
  }

  private initialiseSimulation() {
    this.simulation.on('tick', this.simulationTicked)
    this.simulation.on('end', this.simulationEnded)
  }

  private configUpdated() {
    this.refreshEntities()
    this.updateLayout()
  }

  private storyPathChanged() {
    this.pendingBubblesToRemove = []
  }

  private nodeGroupsUpdated() {
    this.updateHeaders()
    this.updateBubbles()
    this.refreshEntities()
    this.updateLayout()
  }

  private updateHeaders() {
    this.entitiesRegister
      .getEntities(HeaderGroupV2)
      .filter(
        (headerGroup) =>
          !this.headerNodes.some((node) => node.path === headerGroup.path.toString()),
      )
      .forEach((headerGroup) => {
        this.removeGroup(headerGroup)
        this.entitiesRegister.removeEntity(headerGroup)
        this.metadataRegister.removeHeader(headerGroup.path)
      })

    this.headerNodes.forEach((node) => {
      const headerMetadata = this.metadataRegister.getHeader(node.path)
      if (headerMetadata) {
        headerMetadata.updateNode(node)
      } else {
        const headerMetadata = new HeaderMetadata(node)
        const header = new HeaderGroupV2(headerMetadata)
        this.addGroup(header)
        this.metadataRegister.addHeader(headerMetadata)
        this.entitiesRegister.addEntity(header)
      }
    })
  }

  private updateBubbles() {
    const bubblesOperations = new BubblesTransitionOperationsV2()
    const bubblesPathsToRemove = new Set(
      this.metadataRegister.bubbles.map((x) => x.path.toString()),
    )

    this.updateMainBubbles(bubblesOperations, bubblesPathsToRemove)
    this.updateSideBubbles(bubblesOperations, bubblesPathsToRemove)

    bubblesPathsToRemove.forEach((path) => {
      const bubble = this.metadataRegister.getBubble(path)
      if (bubble) {
        bubblesOperations.appendBubbleToRemove(bubble)
      }
    })

    this.addBubblesToCanvas(bubblesOperations.toAdd)
    this.removeBubblesFromCanvas(bubblesOperations.toRemove)

    bubblesOperations.amalgamating.forEach((op) => this.handleBubbleAmalgamating(op.add, op.remove))
    bubblesOperations.splitting.forEach((op) => this.handleBubbleSplitting(op.add, op.remove))
  }

  private updateMainBubbles(
    bubblesOperations: BubblesTransitionOperationsV2,
    bubblesPathsToRemove: Set<string>,
  ) {
    const clustersPathsToRemove = new Set(
      this.metadataRegister.clusters.map((x) => x.path.toString()),
    )
    this.nodeGroups.main.forEach((nodeGroup) => {
      let clusterMetadata: ClusterMetadata | undefined
      if (this.isRoot) {
        clusterMetadata = this.rootClusterMetadata
      } else {
        const clusterPath = nodeGroup.ancestorPath
        clusterMetadata = this.metadataRegister.getCluster(clusterPath)
        if (!clusterMetadata) {
          clusterMetadata = new ClusterMetadata(new Path({ path: clusterPath }))
          this.metadataRegister.addCluster(clusterMetadata)
        }
        clusterMetadata.clearBubbles()
        clustersPathsToRemove.delete(clusterPath)
      }

      nodeGroup.nodes.forEach((node) => {
        let bubbleMetadata = this.metadataRegister.getBubble(node.path)
        if (bubbleMetadata) {
          bubbleMetadata.updateNode(node)
        } else {
          bubbleMetadata = new BubbleMetadata(node, this.canvasCenter)
          bubblesOperations.appendBubbleToAdd(bubbleMetadata)
        }
        if (clusterMetadata) {
          clusterMetadata.addBubble(bubbleMetadata)
        }
        bubblesPathsToRemove.delete(node.path.toString())
      })
    })

    clustersPathsToRemove.forEach((path) => {
      this.metadataRegister.removeCluster(path)
    })
  }

  private updateSideBubbles(
    bubblesOperations: BubblesTransitionOperationsV2,
    bubblesPathsToRemove: Set<string>,
  ) {
    this.sideClusterMetadata.clearBubbles()
    this.nodeGroups.side.forEach((node) => {
      let bubbleMetadata = this.metadataRegister.getBubble(node.path)
      if (!bubbleMetadata) {
        bubbleMetadata = new BubbleMetadata(node, this.canvasCenter)
        bubblesOperations.appendBubbleToAdd(bubbleMetadata)
      }
      this.sideClusterMetadata.addBubble(bubbleMetadata)
      bubblesPathsToRemove.delete(node.path.toString())
    })
  }

  private handleBubbleSplitting(
    descendantBubbles: BubbleMetadata[],
    bubbleToSplit: BubbleMetadata,
  ) {
    if (descendantBubbles.length) {
      // Determine the destination of the first descendant Bubble then update the destination
      // position for all of the Bubbles, including the Bubble that is going to be split
      descendantBubbles.forEach((descendant: BubbleMetadata) => {
        descendant.x = bubbleToSplit.x
        descendant.y = bubbleToSplit.y
      })

      this.addBubblesToCanvas(descendantBubbles)
      this.removeBubblesFromCanvas([bubbleToSplit])

      this.updateSimulation()
    }
  }

  /*
   * When descendant bubbles are amalgamating into their common ancestor we want
   * the descendant bubbles to move towards the new ancestor position then
   * when enough of them are close to that position replace the descendant bubbles
   * with the ancestor bubble, this should give the illusion that the bubbles are merging together.
   * To achieve this we create the ancestor bubble but do not render it yet. We then
   * set the descendant bubbles' destination positions to the ancestor bubble's
   * destination position and update the simulation to start moving the descendant bubbles
   * together.
   * As the simulation ticks we then calculate whether enough of them are near their destination,
   * if enough of them are then we remove the descendant bubbles from the canvas and render the ancestor bubble
   */
  private handleBubbleAmalgamating(
    amalgamatedBubble: BubbleMetadata,
    descendantBubbles: BubbleMetadata[],
  ) {
    amalgamatedBubble.x = amalgamatedBubble.destinationPosition.x
    amalgamatedBubble.y = amalgamatedBubble.destinationPosition.y

    descendantBubbles.forEach((descendant: BubbleMetadata) => {
      descendant.destinationPosition = amalgamatedBubble.destinationPosition
    })

    this.pendingBubbleAmalgamations.push({
      ancestor: amalgamatedBubble,
      descendants: descendantBubbles,
    })
    this.pendingBubblesToRemove.push(...descendantBubbles)
  }

  private amalgamateBubbles(bubbleAmalgamation: BubbleAmalgamation) {
    this.pendingBubbleAmalgamations = this.pendingBubbleAmalgamations.filter(
      (x) => x.ancestor !== bubbleAmalgamation.ancestor,
    )

    this.addBubblesToCanvas([bubbleAmalgamation.ancestor])
    this.removeBubblesFromCanvas(bubbleAmalgamation.descendants)

    this.updateSimulation()
  }

  private simulationTicked() {
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      if (shouldBubblesAmalgamate(bubbleAmalgamation.descendants, this.minimumCanvasSideLength)) {
        this.amalgamateBubbles(bubbleAmalgamation)
      }
    }
  }

  private simulationEnded() {
    // Amalgamate any Bubbles that should have already been amalgamated but haven't for whatever reason
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      this.amalgamateBubbles(bubbleAmalgamation)
    }
    // Remove any Bubbles that should have already been removed but haven't
    this.removeBubblesFromCanvas(this.pendingBubblesToRemove)
    this.pendingBubblesToRemove = []
  }

  private updateSimulation() {
    this.simulation.updateSimulation(this.metadataRegister.bubbles)
  }

  private addBubblesToCanvas(bubbles: BubbleMetadata[]) {
    bubbles.forEach((bubble: BubbleMetadata) => {
      const bubbleGroupV2 = new BubbleGroupV2(bubble)
      this.addGroup(bubbleGroupV2)

      // When a simulation is already running and Bubbles are waiting to
      // amalgamate we may already have a Bubble rendered for the given path,
      // we need to ensure that it is removed before attempting to render the new bubble
      this.removeBubblesFromCanvas([bubble])

      this.metadataRegister.addBubble(bubble)
      this.entitiesRegister.addEntity(bubbleGroupV2)
    })
  }

  private removeBubblesFromCanvas(bubbles: BubbleMetadata[]) {
    bubbles.forEach((bubble: BubbleMetadata) => {
      const bubbleGroup = this.entitiesRegister.getEntity(BubbleGroupV2, bubble.path.toString())
      if (bubbleGroup) {
        this.removeGroup(bubbleGroup)
        this.entitiesRegister.removeEntity(bubbleGroup)
      }

      this.metadataRegister.removeBubble(bubble.path)
    })
  }

  private refreshEntities() {
    this.entitiesRegister.allEntities().forEach((entity: Shapes.Entity) => {
      entity.refresh()
    })
  }

  private updateRootBubblesDestinationPositions() {
    this.metadataRegister.bubbles.forEach((bubble) => {
      // If loading, dampen the initial burst by randomly scattering
      // the bubbles over a wider area
      bubble.destinationPosition = this.loading
        ? {
            x: this.rootBubblesPosition.x + Math.floor(Math.random() * 201) - 100,
            y: this.rootBubblesPosition.y + Math.floor(Math.random() * 201) - 100,
          }
        : {
            x: this.rootBubblesPosition.x,
            y: this.rootBubblesPosition.y,
          }
    })
  }

  private updateLayout() {
    let clusterBoxes: ClusterAndHeaderBoxMetadata[]
    if (this.isRoot) {
      clusterBoxes = [
        new ClusterAndHeaderBoxMetadata({ cluster: this.rootClusterMetadata, padding: 1500 }),
      ]
    } else {
      clusterBoxes = this.metadataRegister.clusters.map((cluster) => {
        const header = this.metadataRegister.getHeader(cluster.path)
        return new ClusterAndHeaderBoxMetadata({ cluster, header })
      })
      const { sortBy, direction } = this.config.clustersSorting
      clusterBoxes = sortClusters(clusterBoxes, sortBy, direction)
    }

    this.layout = calculateLayout(
      clusterBoxes,
      this.sideClusterMetadata.boundingBoxSize,
      this.config.bubbleParams.hideSideBubblesPercentage,
      this.minimumCanvasSideLength,
      this.containerSizeOnCanvas,
      this.containerToCanvasScale,
    )
    this.recenter()

    if (this.isRoot) {
      this.rootBubblesPosition = this.layout.allEntitiesBoundingBox.center
      this.updateRootBubblesDestinationPositions()
    } else {
      Object.keys(this.layout.mainClustersPositionsByPath).forEach((path) => {
        const mainClustersPositions = this.layout!.mainClustersPositionsByPath[path]

        const cluster = this.metadataRegister.getCluster(path)
        if (cluster) {
          cluster.position = mainClustersPositions.cluster
        }

        const header = this.metadataRegister.getHeader(path)
        if (header) {
          header.position = mainClustersPositions.header
        }
      })
    }

    this.allEntitiesBoundingBox.update(this.layout.allEntitiesBoundingBox)
    this.mainClustersBoundingBox.update(this.layout.mainClustersBoundingBox)
    this.sideBubblesClusterBoundingBox.update(this.layout.sideBubblesClusterBoundingBox)
    this.sideClusterMetadata.position = this.layout.sideBubblesClusterBoundingBox.center

    this.updateSimulation()
  }
}
</script>
